import { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import {
  copiarCampoGrid,
  eliminarFilaGrid,
  estadoInicial,
  setModal
} from 'slices/modalSlice'
import { GenericModal } from '../reutilizables/modal/GenericModal.jsx'
import { Paginacion } from '../reutilizables/paginacion/Paginacion.jsx'
import {
  actualizarLista,
  buscarListaPorVendedor,
  eliminarLista,
  insertarLista
} from '../servicios/listaEnvioService.js'
import BordePrincipal from '../ui/BordePrincipal.jsx'
import { ItemLista } from './ItemLista'
import styles from './Lista.module.css'

const campos = [
  {
    type: 'text',
    name: 'nombreLista',
    placeholder: 'Ingrese el nombre la lista',
    label: 'Nombre de la lista',
    isFullWidth: true,
    isEditable: false,
    isGrid: false
  },
  {
    type: 'date',
    name: 'vigenciaDesde',
    label: 'Comienzo de la vigencia',
    isFullWidth: false,
    isEditable: false,
    isGrid: false
  },
  {
    type: 'date',
    name: 'vigenciaHasta',
    label: 'Fin de la vigencia',
    isFullWidth: false,
    isEditable: true,
    isGrid: false
  },
  {
    type: 'text',
    name: 'zona',
    placeholder: 'Zona del envio',
    label: 'Zona',
    isFullWidth: false,
    isEditable: true,
    isGrid: true,
    copy: false
  },
  {
    type: 'number',
    name: 'cantidadDesde',
    label: 'Cantidad desde',
    isFullWidth: false,
    isEditable: true,
    isGrid: true,
    copy: false
  },
  {
    type: 'number',
    name: 'cantidadHasta',
    label: 'Cantidad hasta',
    isFullWidth: false,
    isEditable: true,
    isGrid: true,
    copy: false
  },
  {
    type: 'number',
    name: 'costo',
    label: 'Costo (sin IVA)',
    isFullWidth: false,
    isEditable: true,
    isGrid: true,
    copy: false
  },
  {
    type: 'number',
    name: 'tEstimado',
    label: 'Dias entrega',
    isFullWidth: false,
    isEditable: true,
    isGrid: true,
    copy: false
  },
  {
    type: 'select',
    name: 'moneda',
    placeholder: 'Seleccione una',
    label: 'Moneda',
    isFullWidth: false,
    isEditable: true,
    isGrid: true,
    copy: true,
    options: [
      { value: 'ARS', label: 'ARS ($)', key: 1 },
      { value: 'USD', label: 'USD (U$S)', key: 2 },
      { value: 'EUR', label: 'EUR (€)', key: 3 }
    ]
  }
]

const PAGINA_ITEMS = 10

export const Lista = () => {
  const [paginaActual, setPaginaActual] = useState(1)
  const [itemsPorPagina, setItemsPorPagina] = useState(PAGINA_ITEMS)
  const [lista, setLista] = useState([])
  const [listaActual, setListaActual] = useState([])
  const topRef = useRef(null)
  const [listaAEliminar, setListaAEliminar] = useState(null)
  const [actualizar, setActualizar] = useState(false)
  const [ordenNombre, setOrdenNombre] = useState(null)

  const dispatch = useDispatch()

  const { editMode, values, errores, type } = useSelector((state) => state.genericModal)

  const idVendedor = values.idVendedor

  useEffect(() => {
    dispatch(setModal({ campos: campos }))
  }, [])

  const handleChange = (e) => {
    const { name, value, filaIndex = '' } = e.target
    const campo = filaIndex !== '' ? `${name}_${filaIndex}` : name
    const nuevasValidaciones = { ...errores }

    if (filaIndex === '') {
      dispatch(setModal({ values: { ...values, [name]: value } }))
    } else {
      const copia = [...values.grilla]
      copia[filaIndex] = { ...copia[filaIndex], [name]: value }
      dispatch(setModal({ values: { grilla: copia } }))
    }

    if (errores[campo]) {
      delete nuevasValidaciones[campo]
    }
    dispatch(setModal({ errores: nuevasValidaciones }))
  }

  const formatearFecha = (fecha) => {
    if (!fecha) {
      return ''
    }
    return fecha.split('T')[0]
  }

  const editarLista = (item) => {
    const filas = (item.grilla || [])
      .sort((a, b) => a.zona.toLowerCase().localeCompare(b.zona.toLowerCase()))
      .map((fila, idx) => ({
        zona: fila.zona ?? '',
        cantidadDesde: Number(fila.cantidadDesde ?? 0),
        cantidadHasta: Number(fila.cantidadHasta ?? 0),
        costo: Number(fila.costo ?? 0),
        moneda: fila.moneda ?? '',
        tEstimado: Number(fila.tEstimado ?? 0),
        posicion: fila.posicion ?? idx + 1
      }))

    dispatch(
      setModal({
        size: 'lg',
        title: 'Editar lista',
        type: 'form',
        body: '',
        show: true,
        editMode: true,
        values: {
          idLista: item.idLista,
          idVendedor: item.idVendedor,
          nombreLista: item.nombreLista,
          vigenciaDesde: formatearFecha(item.vigenciaDesde),
          vigenciaHasta: formatearFecha(item.vigenciaHasta),
          vigenciaHastaOriginal: formatearFecha(item.vigenciaHasta),
          zona: item.zona,
          grilla: filas.length ? filas : estadoInicial.grilla
        }
      })
    )
  }

  const eliminarListaPagina = (item) => {
    dispatch(
      setModal({
        size: 'sm',
        title: 'Eliminar lista',
        type: 'botones',
        body: '¿Estás seguro que deseas eliminar la lista?',
        show: true
      })
    )
    setListaAEliminar(item)
  }

  const abrirModal = () => {
    dispatch(
      setModal({
        size: 'lg',
        title: 'Crear lista',
        type: 'form',
        body: '',
        show: true,
        editMode: false
      })
    )
  }

  const handleConfirmEliminar = async () => {
    if (!listaAEliminar) {
      return
    }
    try {
      await eliminarLista(listaAEliminar.idLista, listaAEliminar.idVendedor)
      setListaAEliminar(null)
      setActualizar((prev) => !prev)
      return true
    } catch {
      toast.error('Ocurrió un error al eliminar la lista.')
    }
  }

  const handleConfirmModal = async () => {
    const errores = {}

    const { nombreLista, vigenciaDesde, vigenciaHasta } = values

    if ((!nombreLista || nombreLista.trim() === '') && !editMode) {
      errores.nombreLista = 'El nombre es obligatorio.'
    }

    if (!vigenciaDesde && !editMode) {
      errores.vigenciaDesde = 'Debe seleccionar una fecha de comienzo de vigencia.'
    } else if (!editMode) {
      const fechaDesdeStr = vigenciaDesde
      const fechaActualStr = new Date().toISOString().split('T')[0]

      if (fechaDesdeStr < fechaActualStr) {
        errores.vigenciaDesde =
          'La fecha de inicio debe ser igual o posterior a la fecha actual.'
      }
    }

    if (!vigenciaHasta) {
      errores.vigenciaHasta = 'La fecha de fin de vigencia es obligatoria.'
    } else if (vigenciaHasta <= vigenciaDesde) {
      errores.vigenciaHasta =
        'La fecha de fin de vigencia debe ser mayor que la de comienzo.'
    } else if (editMode && vigenciaHasta < values.vigenciaHastaOriginal) {
      errores.vigenciaHasta =
        'No puede establecer una fecha anterior a la vigencia original.'
    }

    values.grilla.forEach((fila, i) => {
      const { cantidadDesde, cantidadHasta, costo, moneda, tEstimado, zona } = fila

      if (!zona || zona.trim() === '') {
        errores[`zona_${i}`] = 'La zona es obligatoria.'
      }

      if (cantidadDesde < 0 || !cantidadDesde) {
        errores[`cantidadDesde_${i}`] = 'Debe ingresar una cantidad mínima.'
      }

      if (i > 0) {
        const zonaActual = fila.zona?.trim().toUpperCase()
        const desdeActual = Number(cantidadDesde)

        const filasMismaZona = values.grilla
          .filter((f, idx) => idx < i && f.zona?.trim().toUpperCase() === zonaActual)
          .sort((a, b) => Number(a.cantidadDesde) - Number(b.cantidadDesde))

        if (filasMismaZona.length > 0) {
          const filaAnterior = filasMismaZona[filasMismaZona.length - 1]
          const hastaAnterior = Number(filaAnterior.cantidadHasta)

          if (desdeActual !== hastaAnterior + 1) {
            errores[`cantidadDesde_${i}`] =
              `En la zona "${fila.zona}", la cantidad desde debe ser ${hastaAnterior + 1} para evitar brechas.`
          }
        }
      }

      const desde = Number(cantidadDesde)
      const hasta = Number(cantidadHasta)

      if (!hasta || hasta <= desde) {
        errores[`cantidadHasta_${i}`] = 'La cantidad máxima debe ser mayor que la mínima.'
      }

      if (costo <= 0 || !costo) {
        errores[`costo_${i}`] = 'Debe ingresar el valor del costo.'
      }

      if (!moneda) {
        errores[`moneda_${i}`] = 'Debe seleccionar una moneda.'
      }

      if (tEstimado <= 0 || !tEstimado) {
        errores[`tEstimado_${i}`] = 'Debe ingresar el tiempo estimado.'
      }
    })

    if (
      !editMode &&
      lista.some(
        (l) =>
          l.nombreLista.trim().toLowerCase() === values.nombreLista.trim().toLowerCase()
      )
    ) {
      errores.nombreLista = 'Ya existe una lista con este nombre.'
    }

    dispatch(
      setModal({
        errores: errores,
        type: 'form'
      })
    )

    if (Object.keys(errores).length > 0) {
      return false
    }
    try {
      if (editMode) {
        await handleActualizar()
      } else {
        await handleConfirmarAgregado()
      }
      return true
    } catch {
      toast.error('Ocurrió un error inesperado.')
      return false
    }
  }

  const handleActualizar = async () => {
    try {
      const listasAEnviar = values.grilla.map((fila, index) => ({
        nombreLista: values.nombreLista,
        vigenciaDesde: values.vigenciaDesde,
        vigenciaHasta: values.vigenciaHasta,
        idVendedor: values.idVendedor,
        grilla: [
          {
            zona: fila.zona,
            cantidadDesde: Number(fila.cantidadDesde),
            cantidadHasta: Number(fila.cantidadHasta),
            costo: Number(fila.costo),
            moneda: fila.moneda,
            tEstimado: Number(fila.tEstimado),
            posicion: index + 1
          }
        ]
      }))

      await actualizarLista(listasAEnviar)
      dispatch(setModal({ show: false }))
      setActualizar((prev) => !prev)
    } catch {
      toast.error('Ocurrió un error al actualizar la lista.')
    }
  }

  const handleConfirmarAgregado = async () => {
    const nuevaLista = {
      nombreLista: values.nombreLista,
      vigenciaDesde: values.vigenciaDesde,
      vigenciaHasta: values.vigenciaHasta,
      idVendedor: values.idVendedor,
      grilla: []
    }

    values.grilla.forEach((fila) => {
      nuevaLista.grilla.push({
        zona: fila.zona,
        cantidadDesde: fila.cantidadDesde,
        cantidadHasta: fila.cantidadHasta,
        costo: fila.costo,
        moneda: fila.moneda,
        tEstimado: fila.tEstimado
      })
    })
    try {
      await insertarLista(nuevaLista)
      setActualizar((prev) => !prev)
      dispatch(setModal({ show: false }))
    } catch {
      toast.error('Ocurrió un error al agregar la lista.')
    }
  }

  const traerInfo = async () => {
    try {
      const { data } = await buscarListaPorVendedor(values.idVendedor)
      const unificadas = Object.values(
        data.reduce((acc, item) => {
          const key = item.nombreLista.trim().toLowerCase()

          if (!acc[key]) {
            acc[key] = {
              idLista: item.idLista,
              nombreLista: item.nombreLista,
              vigenciaDesde: item.vigenciaDesde,
              vigenciaHasta: item.vigenciaHasta,
              idVendedor: item.idVendedor,
              grilla: []
            }
          }

          if (Array.isArray(item.grilla)) {
            acc[key].grilla.push(...item.grilla)
          }

          return acc
        }, {})
      ).map((l) => ({
        ...l,
        contador: l.grilla.length
      }))

      setLista(unificadas)
    } catch {
      toast.error('Ocurrió un error al cargar las listas.')
    }
  }

  useEffect(() => {
    traerInfo()
  }, [actualizar, idVendedor])

  const ordenarLista = (lista, orden) => {
    if (!orden) {
      return lista
    }
    return [...lista].sort((a, b) => {
      if (a.nombreLista.toLowerCase() < b.nombreLista.toLowerCase()) {
        return orden === 'asc' ? -1 : 1
      }

      if (a.nombreLista.toLowerCase() > b.nombreLista.toLowerCase()) {
        return orden === 'asc' ? 1 : -1
      }
      return 0
    })
  }

  useEffect(() => {
    const listaOrdenada = ordenarLista(lista, ordenNombre)
    const indiceUltimoItem = paginaActual * itemsPorPagina
    const indicePrimerItem = indiceUltimoItem - itemsPorPagina
    setListaActual(listaOrdenada.slice(indicePrimerItem, indiceUltimoItem))
  }, [lista, paginaActual, itemsPorPagina, ordenNombre])

  const totalPaginas = Math.ceil(lista.length / itemsPorPagina)

  const agregarFila = () => {
    const nuevaFila = {
      zona: '',
      cantidadDesde: 0,
      cantidadHasta: 0,
      costo: 0,
      moneda: '',
      tEstimado: 0
    }

    dispatch(
      setModal({
        values: {
          grilla: [...(values.grilla || []), nuevaFila]
        }
      })
    )
  }

  const eliminarFila = (index) => {
    dispatch(eliminarFilaGrid(index))
  }

  const copiarCampo = (name, value) => {
    dispatch(copiarCampoGrid({ name, value }))
  }

  return (
    <div className={styles.listaContainer} ref={topRef}>
      <BordePrincipal titulo="Mis Listas">
        <>
          <div className="container">
            <div className="row">
              <div className={`${styles.ordenamientoContainer} mb-3`}>
                <button
                  onClick={() => setOrdenNombre('asc')}
                  className={`${styles.btn} ${styles.btnOutlinePrimary}`}
                >
                  A-Z
                </button>
                <button
                  onClick={() => setOrdenNombre('desc')}
                  className={`${styles.btn} ${styles.btnOutlinePrimary}`}
                >
                  Z-A
                </button>
                <button
                  onClick={() => setOrdenNombre(null)}
                  className={`${styles.btn} ${styles.btnOutlinePrimary}`}
                >
                  Limpiar
                </button>
              </div>
              {listaActual.map((item, index) => (
                <div className="col-12 col-md-4 mb-3" key={item.idLista ?? index}>
                  <ItemLista
                    lista={item}
                    onEditar={() => editarLista(item)}
                    onEliminar={() => eliminarListaPagina(item)}
                  />
                </div>
              ))}
              <div className="col-12 col-md-4 mb-3">
                <div
                  className={`${styles.card} ${styles['plus-card']}`}
                  onClick={abrirModal}
                >
                  +
                </div>
              </div>
              <GenericModal
                onConfirm={type === 'form' ? handleConfirmModal : handleConfirmEliminar}
                onChange={handleChange}
                onAddItem={agregarFila}
                onRemoveItem={eliminarFila}
                onCopyField={copiarCampo}
              />
            </div>
          </div>

          {totalPaginas > 0 && (
            <Paginacion
              totalPaginas={totalPaginas}
              paginaActual={paginaActual}
              onCambioPagina={(num) => {
                setPaginaActual(num)
                topRef.current?.scrollIntoView({ behavior: 'smooth' })
              }}
              itemsPorPagina={itemsPorPagina}
              onCambioItemsPorPagina={(num) => {
                setItemsPorPagina(num)
                setPaginaActual(1)
                topRef.current?.scrollIntoView({ behavior: 'smooth' })
              }}
            />
          )}
        </>
      </BordePrincipal>
    </div>
  )
}
